import { DateRange, Trip } from '@types'
import {
	addDays,
	dateAtDayIndex,
	dayIndexOf,
	eachDay,
	formatCalendarDate,
	formatWeekday,
	isValidCalendarDate,
	parseCalendarDate,
	toCompact,
	toDashed,
} from '@lib/datetime'

/**
 * TripHelper class provides utility methods for trip-related operations
 * Consolidates duplicate date formatting, parsing, and calculation logic.
 * All calendar-date math delegates to @lib/datetime so it is UTC-stable.
 */
export class TripHelper {
	private readonly trip: Trip

	constructor(trip: Trip) {
		this.trip = trip
	}

	/**
	 * Formats a date string from YYYYMMDD format to a human-readable format
	 * @param dateString - Date in YYYYMMDD format (can be null)
	 * @returns Formatted date string or fallback text
	 */
	static formatDate(dateString: string | null): string {
		if (!dateString) return 'No dates set'
		return formatCalendarDate(dateString, 'medium') || 'Invalid date'
	}

	/**
	 * Parses YYYYMMDD format date string to a Date object
	 * @param dateString - Date in YYYYMMDD format
	 * @returns Date object (UTC-built) or null if invalid
	 */
	static parseDate(dateString: string): Date | null {
		const p = parseCalendarDate(dateString)
		if (!p) return null
		return new Date(Date.UTC(p.year, p.month - 1, p.day))
	}

	/**
	 * Gets the trip start date as a Date object
	 * @returns Date object or null if startDate is not available
	 */
	getStartDate(): Date | null {
		return TripHelper.parseDate(this.trip.startDate || '')
	}

	/**
	 * Gets the trip end date as a Date object
	 * @returns Date object or null if startDate or dayCount is not available
	 */
	getEndDate(): Date | null {
		if (!isValidCalendarDate(this.trip.startDate) || !this.trip.dayCount) return null
		const endCompact = toCompact(addDays(this.trip.startDate, this.trip.dayCount - 1))
		return TripHelper.parseDate(endCompact)
	}

	/**
	 * Gets the trip start date formatted for display
	 * @returns Formatted date string
	 */
	getFormattedStartDate(): string {
		return TripHelper.formatDate(this.trip.startDate)
	}

	/**
	 * Gets the trip end date formatted for display
	 * @returns Formatted date string
	 */
	getFormattedEndDate(): string {
		if (!isValidCalendarDate(this.trip.startDate) || !this.trip.dayCount) return 'No dates set'
		const endCompact = toCompact(addDays(this.trip.startDate, this.trip.dayCount - 1))
		return formatCalendarDate(endCompact, 'medium') || 'No dates set'
	}

	/**
	 * Calculates the actual date from trip start date and tripDayIndex
	 * @param tripDayIndex - 0-based day index
	 * @returns Date string in YYYY-MM-DD format
	 */
	calculateDateFromDayIndex(tripDayIndex: number): string {
		if (!isValidCalendarDate(this.trip.startDate)) return ''
		return dateAtDayIndex(this.trip.startDate, tripDayIndex)
	}

	/**
	 * Calculates the tripDayIndex from a date string and trip start date
	 * @param eventDate - Date string in YYYY-MM-DD format
	 * @returns tripDayIndex (0-based day index)
	 */
	calculateDayIndexFromDate(eventDate: string): number {
		if (!isValidCalendarDate(this.trip.startDate)) return 0
		return dayIndexOf(eventDate, this.trip.startDate)
	}

	/**
	 * Gets the trip duration in days
	 * @returns Number of days or 0 if not available
	 */
	getDuration(): number {
		return this.trip.dayCount || 0
	}

	/**
	 * Checks if the trip has valid date information
	 * @returns true if trip has startDate and dayCount
	 */
	hasValidDates(): boolean {
		return !!(this.trip.startDate && this.trip.dayCount && this.trip.dayCount > 0)
	}

	/**
	 * Gets the trip date range as ISO date strings
	 * @returns DateRange object with startDate and endDate in YYYY-MM-DD format
	 */
	getDateRange(): DateRange | null {
		if (!isValidCalendarDate(this.trip.startDate) || !this.trip.dayCount) return null

		return {
			startDate: toDashed(this.trip.startDate),
			endDate: addDays(this.trip.startDate, this.trip.dayCount - 1),
		}
	}

	/**
	 * Generates an array of day objects for the trip
	 * @returns Array of day objects with id, date, and displayName
	 */
	generateDays(): Array<{ id: string; date: string; displayName: string }> {
		if (!isValidCalendarDate(this.trip.startDate) || !this.trip.dayCount) return []

		const startDashed = toDashed(this.trip.startDate)
		const endDashed = addDays(this.trip.startDate, this.trip.dayCount - 1)

		return eachDay(startDashed, endDashed).map(date => ({
			id: `day-${date}`,
			date,
			displayName: formatWeekday(date, 'long'),
		}))
	}

	/**
	 * Extracts date string from dayId
	 * @param dayId - Day ID in format "day-YYYY-MM-DD"
	 * @returns Date string in YYYY-MM-DD format
	 */
	static extractDateFromDayId(dayId: string): string {
		return dayId.replace('day-', '')
	}

	/**
	 * Converts trip date from YYYYMMDD format to YYYY-MM-DD format for HTML date inputs
	 * @param tripDate - Date in YYYYMMDD format (can be null)
	 * @returns Date in YYYY-MM-DD format or empty string if invalid
	 */
	static convertTripDateToInputFormat(tripDate: string | null): string {
		return toDashed(tripDate)
	}
}

// Export static methods for backward compatibility
export const formatTripDate = TripHelper.formatDate
export const parseTripDate = TripHelper.parseDate
export const extractDateFromDayId = TripHelper.extractDateFromDayId
export const convertTripDateToInputFormat = TripHelper.convertTripDateToInputFormat
